import React from 'react';
import {
  Box, HStack, Heading, Icon, Pressable, ScrollView, Text, VStack,
} from '@gluestack-ui/themed';
import { ArrowLeft } from 'lucide-react-native';

import { useRefetchModuleTasks } from '@/common/hooks/student/useRefetchModuleTasks';
import { useGuardStudentAction } from '@/common/studentPreview/StudentPreviewSource';
import { OralExamQuestionPoolSection } from '@/student/module/components/OralExamQuestionPoolSection';
import EventTaskView from '@/student/module/components/task-types/event/EventTaskView';
import { OralExamTakenNotice } from '../components/OralExamTakenNotice';
import { useOralExamTaken } from '../hooks/useOralExamTaken';
import type { Module, Resource, Task } from '../types';

interface OralExamPrepWebLayoutProps {
  task: Task;
  resources: Resource[];
  module: Module;
  tasks: Task[];
  testID?: string;
  courseId: string;
  moduleId: string;
  onBack: () => void;
}

/**
 * Desktop TaskScreen for oral exams: the full question pool on the left,
 * session / materials / dry run on the right. Not the essay composer.
 * Once the exam is taken this page only says so and leads back to the module.
 */
const OralExamPrepWebLayout: React.FC<OralExamPrepWebLayoutProps> = ({
  task,
  resources,
  module,
  tasks,
  testID,
  courseId,
  moduleId,
  onBack,
}) => {
  const refetchModuleTasks = useRefetchModuleTasks(courseId, moduleId);
  const guardStudentAction = useGuardStudentAction();
  const handleRetryQuestionPool = () => {
    guardStudentAction(() => {
      refetchModuleTasks();
    });
  };
  const isExamTaken = useOralExamTaken(task, courseId, moduleId, module?.version);

  const header = (
    <HStack
      h={72}
      alignItems="center"
      space="md"
      px="$6"
      borderBottomWidth={1}
      borderColor="$borderLight"
    >
      <Pressable
        onPress={onBack}
        p="$2"
        borderRadius="$md"
        sx={{ ':hover': { bg: '$bgHover' } }}
        testID="oral-exam-prep-back"
      >
        <Icon as={ArrowLeft} color="$textBody" size="lg" />
      </Pressable>
      <VStack flex={1}>
        <Text size="xs" color="$textHint" textTransform="uppercase" letterSpacing="$md" numberOfLines={1}>
          {module?.title || 'MODULE'}
        </Text>
        <Heading testID="task-title" size="sm" color="$textHeading" numberOfLines={2}>
          {task.title}
        </Heading>
      </VStack>
    </HStack>
  );

  const sessionRail = (
    <EventTaskView
      task={task}
      module={module}
      tasks={tasks}
      selectedModule={{ courseId, moduleId }}
      resources={resources}
      showTitle={false}
      noCard
      poolSurface="hidden"
    />
  );

  if (isExamTaken) {
    return (
      <Box flex={1} bg="$bgApp" testID={testID} overflow="hidden">
        <VStack flex={1} testID="oral-exam-prep-layout">
          {header}
          <OralExamTakenNotice onBack={onBack} />
        </VStack>
      </Box>
    );
  }

  return (
    <Box flex={1} bg="$bgApp" testID={testID} overflow="hidden">
      <HStack flex={1} testID="oral-exam-prep-layout">
        <VStack flex={1.7} borderRightWidth={1} borderColor="$borderLight" bg="$bgApp">
          {header}
          <ScrollView
            flex={1}
            showsVerticalScrollIndicator={false}
            // eslint-disable-next-line react-native/no-inline-styles
            contentContainerStyle={{ paddingVertical: 24, paddingHorizontal: 24 }}
          >
            <OralExamQuestionPoolSection task={task} surface="full" onRetry={handleRetryQuestionPool} />
          </ScrollView>
        </VStack>

        <Box w={400} maxWidth="40%" bg="$bgCard">
          <ScrollView
            flex={1}
            showsVerticalScrollIndicator={false}
            // eslint-disable-next-line react-native/no-inline-styles
            contentContainerStyle={{ paddingVertical: 24, paddingHorizontal: 20 }}
          >
            {sessionRail}
          </ScrollView>
        </Box>
      </HStack>
    </Box>
  );
};

export default OralExamPrepWebLayout;
